import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { useMessengerScopeSubscribe } from "../app/messenger/MessengerSocketContext";
import { invalidateOfferAccountCaches } from "../app/services/offerAccountCache";
import type {
  DmThreadUpdatedPayload,
  MessengerIncomingEvent,
} from "../types/messengerEvents";

// status / prev_status がともにこの集合のときは「ドラフト編集（autosave）」とみなし、
// 残高・進行中の再取得をスキップする。
const DRAFT_STATUSES = new Set<string>(["new", "draft"]);

/**
 * オファースレッド（subcontract）の状態更新を WS で受け取り、
 * アカウントポップオーバーの残高（CurrentUser）と進行中一覧（Transactions）を
 * リロードなしで最新化する。
 *
 * 操作者本人は `updateDmThread` の `onQueryStarted` で無効化されるため、
 * 本フックは相手側（WS は操作者を exclude する）の即時反映を担う。
 * 純粋なドラフト編集（status / prev_status がともに new/draft）だけは
 * 残高に影響しないため無駄な再取得を避けてスキップする。
 */
export const useOfferAccountSync = (): void => {
  const dispatch = useDispatch();

  const handleDmEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      if (ev.type !== "dm.thread.updated") return;

      const payload = ev.payload as DmThreadUpdatedPayload;
      if (payload.type_offer !== "subcontract") return;

      // 旧サーバで status / prev_status が無い場合は安全側に倒して無効化する。
      const status = payload.status;
      const prevStatus = payload.prev_status;
      const isDraftOnlyEdit =
        !!status &&
        !!prevStatus &&
        DRAFT_STATUSES.has(status) &&
        DRAFT_STATUSES.has(prevStatus);
      if (isDraftOnlyEdit) return;

      invalidateOfferAccountCaches(dispatch);
    },
    [dispatch],
  );

  useMessengerScopeSubscribe("dm", handleDmEvent);
};
